import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AUTH_URL } from '../../config/env';
import { applyAuthHeaders } from '../../utils/authHeaders';
import { createBaseQueryWithReauth } from './baseQueryWithReauth';

// base query for admin API (uses auth service baseUrl)
const adminBaseQuery = fetchBaseQuery({
  baseUrl: AUTH_URL,
  // credentials: 'include', // IMPORTANT: sends HttpOnly cookie for refresh token
  prepareHeaders: (headers) => applyAuthHeaders(headers),
});

// wrap with reauth logic (proactive + reactive token refresh)
const adminBaseQueryWithReauth = createBaseQueryWithReauth({
  baseQuery: adminBaseQuery,
});

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: adminBaseQueryWithReauth,
  tagTypes: ['Services', 'IdentitySync'],
  endpoints: (builder) => ({
    // list all services
    listServices: builder.query<any[], void>({
      query: () => '/admin/services',
      transformResponse: (response: { success?: boolean; data?: any[] }) => {
        if (response && typeof response === 'object' && 'data' in response) {
          return Array.isArray(response.data) ? response.data : [];
        }
        return [];
      },
      providesTags: ['Services'],
    }),

    // get service by id
    getService: builder.query<any, string>({
      query: (clientId) => `/admin/services/${clientId}`,
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      providesTags: (_result, _error, clientId) => [{ type: 'Services', id: clientId }],
    }),

    // create service
    createService: builder.mutation<any, any>({
      query: (body) => ({
        url: '/admin/services',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      invalidatesTags: ['Services'],
    }),

    // update service
    updateService: builder.mutation<any, { clientId: string; data: any }>({
      query: ({ clientId, data }) => ({
        url: `/admin/services/${clientId}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Services', id: clientId },
        'Services',
      ],
    }),

    // delete service
    deleteService: builder.mutation<void, string>({
      query: (clientId) => ({
        url: `/admin/services/${clientId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Services'],
    }),

    getUsersSync: builder.query<any, void>({
      query: () => '/admin/api/users-sync',
      transformResponse: (response: { data?: any }) => response?.data ?? response,
      providesTags: ['IdentitySync'],
    }),

    searchBackendUsers: builder.query<any[], { query: string; limit?: number }>({
      query: ({ query, limit = 20 }) => ({
        url: '/admin/api/backend-users/search',
        params: { query, limit },
      }),
      transformResponse: (response: { data?: any }) => {
        const payload = response?.data ?? response;
        if (payload && typeof payload === 'object' && 'data' in payload) {
          return Array.isArray(payload.data) ? payload.data : [];
        }
        return [];
      },
    }),

    linkIdentityBackendUser: builder.mutation<
    any,
    { identityId: string; backendUserId: string; force?: boolean }
    >({
      query: ({ identityId, backendUserId, force }) => ({
        url: `/admin/api/identities/${identityId}/link-backend-user`,
        method: 'POST',
        body: { backendUserId, force: Boolean(force) },
      }),
      transformResponse: (response: { data?: any }) => {
        const payload = response?.data ?? response;
        return payload?.data ?? payload;
      },
      invalidatesTags: ['IdentitySync'],
    }),

    /** insert users doc with _id === identity.userId (when auth was provisioned but backend row missing) */
    provisionBackendUserForIdentity: builder.mutation<
      { created: boolean; userId: string },
      { identityId: string }
    >({
      query: ({ identityId }) => ({
        url: `/admin/api/identities/${identityId}/provision-backend-user`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: { data?: any }) => {
        const payload = response?.data ?? response;
        return payload?.data ?? payload;
      },
      invalidatesTags: ['IdentitySync'],
    }),
  }),
});

export const {
  useListServicesQuery,
  useGetServiceQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useGetUsersSyncQuery,
  useSearchBackendUsersQuery,
  useLinkIdentityBackendUserMutation,
  useProvisionBackendUserForIdentityMutation,
} = adminApi;
